import { useEffect, useState } from "react";
import useWebPost from "../../utils/useWebPost";
import FullHeightLayout from "../../components/FullHeightLayout";
import { roleTree, roles } from "../../utils/AdminRole";
import CustModal from "../../components/ShowModal";
import { Form, message, Popconfirm, Tree, Typography, Input, Space, Tag, Button, Table } from "antd";
const { Text } = Typography;

const buildParentMap = (treeData, parentMap = {}) => {
    treeData.forEach(node => {
        if (node.children) {
            node.children.forEach(child => {
                parentMap[child.key] = node.key;
            });
            buildParentMap(node.children, parentMap);
        }
    });
    return parentMap;
};
const buildChildrenMap = (treeData, childrenMap = {}) => {
    treeData.forEach(node => {
        if (node.children && node.children.length > 0) {
            childrenMap[node.key] = node.children.map(child => child.key);
            buildChildrenMap(node.children, childrenMap);
        }
    });
    return childrenMap;
};

const parentMap = buildParentMap(roleTree);
const childrenMap = buildChildrenMap(roleTree);

const getCheckedKeysWithDependencies = (checkedKeys) => {
    const keys = Array.isArray(checkedKeys)
        ? checkedKeys
        : checkedKeys?.checked || [];
    const keysSet = new Set(keys);

    keys.forEach(key => {
        let current = key;
        while (parentMap[current]) {
            const parent = parentMap[current];
            if (!keysSet.has(parent)) {
                keysSet.add(parent);
                current = parent;
            } else {
                break;
            }
        }
    });

    const toRemove = new Set();
    const shouldRemoveParent = (parentKey) => {
        const children = childrenMap[parentKey];
        if (!children || children.length === 0) return false;

        const hasAnyChildSelected = children.some(child => {
            if (keysSet.has(child)) return true;
            const checkDescendant = (nodeKey) => {
                const subChildren = childrenMap[nodeKey];
                if (!subChildren) return false;
                return subChildren.some(sub => keysSet.has(sub) || checkDescendant(sub));
            };
            return checkDescendant(child);
        });

        return !hasAnyChildSelected;
    };

    keysSet.forEach(key => {
        if (childrenMap[key] && shouldRemoveParent(key)) {
            toRemove.add(key);
        }
    });

    toRemove.forEach(key => keysSet.delete(key));
    const permt = Array.from(keysSet);
    return permt;
};
function CustRoles() {
    const webPost = useWebPost();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [currentIItem, setCurrentIItem] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchData();
    }, [])
    const fetchData = async () => {
        const ds = await webPost("getCustroles");
        if (ds) {
            setData(ds);
        }
    }

    const columns = [
        { title: '角色', dataIndex: 'name', key: 'name', width: 120 },
        {
            title: '权限',
            dataIndex: 'permission',
            key: 'permission',
            width: 460,
            render: (_, record) => {
                const p = record?.permission;
                if (!p) return <Text type="secondary">未获得任何权限</Text>;
                const ds = typeof p === 'string' ? p.split(',') : p;
                if (ds.includes('all')) {
                    return <Tag color="blue">管理员</Tag>;
                }
                const permissionTags = ds
                    .map((perm) => {
                        const label = roles[perm];
                        return label ? <Tag key={perm}>{label}</Tag> : null;
                    })
                    .filter(Boolean);
                return <Space size="small" wrap>{permissionTags}</Space>;
            },
        },
        {
            title: '操作',
            key: 'action',
            width: 200,
            render: (_, record) => (
                <Space size="middle">
                    <Button size="small" type="link" onClick={() => openModal(record)}>
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定删除该条数据吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button type="link" danger>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ]
    const openModal = (rs) => {
        setModalVisible(true);
        setCurrentIItem(rs);
        if (rs) {
            const permission = Array.isArray(rs.permission) ? rs.permission : typeof rs.permission === 'string'   ? rs.permission.split(',') : [];

            form.setFieldsValue({ ...rs,  permission });
        } else {
            form.resetFields();
        }
    }
    const handleDelete = async (id) => {
        const rs = await webPost("delCustroles", { id });
        if (rs) {
            message.success('删除成功');
            fetchData();
        } else {
            message.error("删除失败");
        }
    }
    const submitForm = async () => {
        const values = await form.validateFields();
        try {
            const response = await webPost('saveCustroles', {
                id: currentIItem?.id || 0, ...values,
                permission: values.permission.join(','),
            });
            if (response) {
                message.success(`${currentIItem?.id > 0 ? '更新' : '添加'}成功`);
                setModalVisible(false);
                fetchData();
            }
        } catch {
            message.error(`${currentIItem?.id > 0 ? '更新' : '添加'}失败`);
        }
    }
    return <FullHeightLayout
        headerContent={<Button type="primary" onClick={() => openModal()}>
            添加角色
        </Button>}
    >
        <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content', y: 'calc(100vh - 32px)' }}
            style={{ marginTop: '8px' }}
        />

        <CustModal
            title={currentIItem?.id > 0 ? '编辑角色' : '添加角色'}
            open={modalVisible}
            onCancel={() => setModalVisible(false)}
            onOk={submitForm}
            width={720}
        >
            <Form
                form={form}
                layout="vertical"
            >
                <Form.Item
                    label="角色名"
                    name="name"
                    rules={[{ required: true, message: '请输入角色' }]}
                >
                    <Input />
                </Form.Item>

                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                    <Form.Item
                        label="权限"
                        name="permission"
                        valuePropName="checkedKeys"
                        trigger="onCheck"
                        rules={[{ required: true, message: '请选择至少一个权限' }]}
                        getValueProps={(value = []) => ({
                            checkedKeys: Array.isArray(value) ? value : [],
                        })}
                        getValueFromEvent={(checkedKeys) => {
                            const newkeys = getCheckedKeysWithDependencies(checkedKeys);
                            console.log(checkedKeys, newkeys);
                            return newkeys;
                        }}
                    >
                        <Tree checkable treeData={roleTree} checkStrictly={true} />
                    </Form.Item>
                </div>
            </Form>
        </CustModal>
    </FullHeightLayout>
}
export default CustRoles;